import React, { useEffect, useState } from 'react';
import { ChevronLeft, Trophy, UserCircle, Loader2, Zap, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase.ts';
import { LeaderboardEntry, AppView } from '../types.ts';
import { getAuraData } from '../constants.tsx';

interface LeaderboardViewProps {
  setActiveView: (view: AppView) => void;
  setViewedProfileId: (id: string) => void;
  currentUserId?: string;
}

type TimeFrame = 'daily' | 'weekly' | 'monthly' | 'all-time';

export default function LeaderboardView({ setActiveView, setViewedProfileId, currentUserId }: LeaderboardViewProps) {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState<TimeFrame>('daily');

  useEffect(() => {
    async function fetchLeaderboard() {
      setIsLoading(true);
      try {
        if (timeframe === 'all-time') {
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .order('xp', { ascending: false })
            .limit(20);
          if (error) throw error;
          setLeaderboard(data as LeaderboardEntry[]);
        } else {
          const days = timeframe === 'daily' ? 1 : timeframe === 'weekly' ? 7 : 30;
          const dateLimit = new Date();
          dateLimit.setDate(dateLimit.getDate() - days);

          const { data: logs, error: logError } = await supabase
            .from('aura_logs')
            .select('user_id, amount')
            .gt('created_at', dateLimit.toISOString());

          if (logError) throw logError;

          const gains: Record<string, number> = {};
          logs?.forEach(log => {
            gains[log.user_id] = (gains[log.user_id] || 0) + log.amount;
          });

          const sortedUserIds = Object.entries(gains)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 20)
            .map(([id]) => id);

          if (sortedUserIds.length === 0) {
            setLeaderboard([]);
          } else {
            const { data: profiles, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .in('id', sortedUserIds);

            if (profileError) throw profileError;

            const finalSorted = sortedUserIds.map(id => {
              const p = profiles?.find(prof => prof.id === id);
              if (!p) return null;
              return {
                ...p,
                xp: gains[id]
              } as LeaderboardEntry;
            }).filter(Boolean) as LeaderboardEntry[];

            setLeaderboard(finalSorted);
          }
        }
      } catch (e) {
        console.error("LEADERBOARD ERROR", e);
      } finally {
        setIsLoading(false);
      }
    }
    fetchLeaderboard();
  }, [timeframe]);

  const handleEntryClick = (id: string) => {
    setViewedProfileId(id);
    setActiveView('profile');
  };

  const timeframeLabels: Record<TimeFrame, string> = {
    daily: "AURA GAINED LAST 24H",
    weekly: "AURA GAINED LAST 7 DAYS",
    monthly: "AURA GAINED LAST 30 DAYS",
    'all-time': "TOTAL AURA OF LEGENDS"
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-in slide-in-from-bottom-8">
      {/* Header Dashboard Card - Restored Original Sizing */}
      <div className="bg-zinc-950/60 backdrop-blur-xl border-2 border-white/5 rounded-[3rem] p-8 shadow-2xl space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <button onClick={() => setActiveView('editor')} className="p-3 bg-zinc-900 rounded-2xl hover:scale-110 active:scale-90 transition-transform shadow-lg">
              <ChevronLeft size={24}/>
            </button>
            <div className="flex flex-col">
              <h2 className="text-5xl font-bungee text-purple-500 uppercase tracking-tighter">TOP AURA</h2>
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest px-1">Global Sigma Ranks</p>
            </div>
          </div>

          <div className="flex items-center gap-2 p-1.5 bg-black/40 rounded-2xl border-2 border-zinc-800 shadow-xl overflow-x-auto whitespace-nowrap scrollbar-hide">
            {(['daily', 'weekly', 'monthly', 'all-time'] as TimeFrame[]).map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-5 py-2.5 rounded-xl font-bungee text-[10px] uppercase transition-all flex items-center gap-2 ${timeframe === tf ? 'bg-purple-600 text-white shadow-lg scale-105' : 'text-zinc-500 hover:text-zinc-300'}`}
                aria-label={`Show ${tf.replace('-', ' ')} leaderboard`}
              >
                {tf === 'daily' ? <Zap size={14}/> : <Calendar size={14}/>}
                {tf.replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 border-l-4 border-purple-500 pl-4 py-1">
          <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">STATUS:</span>
          <span className="text-sm font-bungee text-white uppercase tracking-tight">{timeframeLabels[timeframe]}</span>
        </div>
      </div>

      <div className="bg-zinc-950/80 backdrop-blur-md border-4 border-zinc-800 rounded-[3rem] divide-y-2 divide-zinc-800/50 overflow-hidden shadow-2xl">
        {isLoading ? (
          <div className="p-20 text-center flex flex-col items-center gap-6 opacity-40">
            <Loader2 size={48} className="animate-spin text-purple-500" />
            <span className="font-bungee text-2xl uppercase tracking-widest text-zinc-400">Scanning Sigmas...</span>
          </div>
        ) : leaderboard.length > 0 ? (
          leaderboard.map((entry, idx) => {
            const aura = getAuraData(entry.xp);
            return (
              <div 
                key={entry.id} 
                className={`flex items-center p-6 gap-6 hover:bg-white/5 cursor-pointer transition-all group ${entry.id === currentUserId ? 'bg-purple-900/20' : ''}`}
                onClick={() => handleEntryClick(entry.id)}
              >
                <div className={`w-12 font-bungee text-3xl italic ${idx === 0 ? 'text-yellow-400 drop-shadow-[0_0_10px_rgba(250,196,21,0.5)]' : idx === 1 ? 'text-zinc-300' : idx === 2 ? 'text-orange-400' : 'text-zinc-700'}`}>
                  {idx + 1}
                </div>
                <div className="w-20 h-20 rounded-[2rem] overflow-hidden border-4 border-zinc-800 shrink-0 shadow-lg group-hover:border-purple-500 transition-all bg-zinc-950">
                  {entry.avatar_url ? (
                    <img src={entry.avatar_url} className="w-full h-full object-cover" alt={entry.username} />
                  ) : (
                    <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                      <UserCircle className="text-zinc-700" size={32}/>
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-1">
                  <div className="font-bungee text-2xl uppercase truncate group-hover:text-purple-400 transition-colors">
                    {entry.username || 'Unidentified Sigma'}
                    {entry.id === currentUserId && <span className="ml-2 text-[10px] bg-zinc-800 px-2 py-1 rounded text-zinc-500">YOU</span>}
                  </div>
                  <div className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">{aura.title}</div>
                </div>
                <div className="text-yellow-400 font-bungee text-3xl tabular-nums drop-shadow-sm group-hover:scale-110 transition-transform">
                  {entry.xp.toLocaleString()}
                </div>
              </div>
            );
          })
        ) : (
          <div className="p-20 text-center font-bungee opacity-20 text-3xl uppercase tracking-widest">No Activity Detected</div>
        )}
      </div>
    </div>
  );
}
